import React, { useEffect, useState } from 'react';
import { useInProgressIssueService } from '@atlassian/jira-issue-create-restore/src/services/in-progress-issue/index.tsx';
import { useIssueRestoreMetaService } from '@atlassian/jira-issue-create-restore/src/services/issue-restore-meta/index.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { IssueRestoreProps } from '../../common/types';
import { InProgressIssueStoreContainer } from '../../controllers/in-progress-issue';
import {
	IssueRestoreMetaContainer,
	useIssueRestoreMeta,
} from '../../controllers/issue-restore-meta';
import { InProgressIssueInitialiser } from './in-progress-initialiser';
import { isIndexedDBAvailable } from './utils';

const IssueRestoreInner = ({ children, ...rest }: IssueRestoreProps) => {
	const [{ inProgressIssueMetas, isInitialised }, { init }] = useIssueRestoreMeta();
	const inProgressIssueService = useInProgressIssueService();
	useEffect(() => {
		if (!isInitialised) {
			init();
		}
	}, [isInitialised, init]);

	// To future proof for bulk issue create
	const activeInProgressIssueID = inProgressIssueMetas?.[0]?.inProgressIssueID;
	const activeShouldRestore = inProgressIssueMetas?.[0]?.shouldRestore;

	if (isInitialised) {
		return (
			<InProgressIssueStoreContainer inProgressIssueService={inProgressIssueService}>
				<InProgressIssueInitialiser
					inProgressIssueID={activeInProgressIssueID}
					shouldRestore={activeShouldRestore}
					{...rest}
				>
					{children}
				</InProgressIssueInitialiser>
			</InProgressIssueStoreContainer>
		);
	}
	return null;
};

export const IssueRestore = ({ children, ...rest }: IssueRestoreProps) => {
	const issueRestoreMetaService = useIssueRestoreMetaService();

	const [indexedDBAvailable] = useState(isIndexedDBAvailable());
	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		if (!indexedDBAvailable) {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'indexedDB missing');
		}
	}, [createAnalyticsEvent, indexedDBAvailable]);

	if (!indexedDBAvailable) {
		return <>{children?.({})}</>;
	}

	return (
		<IssueRestoreMetaContainer issueRestoreMetaService={issueRestoreMetaService}>
			<IssueRestoreInner {...rest}>{children}</IssueRestoreInner>
		</IssueRestoreMetaContainer>
	);
};
